// src/components/ProjectsGrid.js

import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import './ProjectsGrid.css';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const MOVEMENT_THRESHOLD = 2;
const HOVER_DELAY = 250;

/* ===== shared image tile (hover effects can be disabled) ===== */
const ImageTile = forwardRef(
  (
    {
      project,
      idx,
      onClick,
      onMouseEnter,
      onMouseMove,
      onMouseLeave,
      opacity,
      disableHover = false,
      dropState,
      onDrop,
      onDragOver,
    },
    ref
  ) => {
    const [loaded, setLoaded] = useState(disableHover);
    const [inView, setInView] = useState(false);
    const wrapperRef = useRef(null);

    const showingPreview =
      dropState &&
      dropState.projectId === project.project_id &&
      dropState.previewUrl;
    const previewUrl = showingPreview && dropState.previewUrl;

    const s3Base =
      'https://arranigoe-website-grid-images.s3.us-east-2.amazonaws.com/';
    const imageUrl = project.project_coverImage
      ? s3Base +
        project.project_coverImage
          .replace('gridTest/', '')
          .replace(/\.jpeg$/i, '.JPEG')
          .replace(/\.png$/i, '.JPEG')
      : null;

    const hasSrc = Boolean(previewUrl || imageUrl);

    // only show spinner when uploading to S3, not during the local preview
    const isUploadInProgress =
      dropState &&
      dropState.projectId === project.project_id &&
      dropState.loading &&
      !showingPreview;

    /* lazy-load: observe wrapper */
    useEffect(() => {
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              setInView(true);
              obs.unobserve(e.target);
            }
          });
        },
        { rootMargin: '200px' }
      );
      if (wrapperRef.current) obs.observe(wrapperRef.current);
      return () => obs.disconnect();
    }, []);

    const isDragOver =
      dropState && dropState.dragOverId === project.project_id;

    return (
      <div
        ref={ref}
        className={[
          'grid-item',
          disableHover ? '' : 'fade-in-item',
          isDragOver ? 'drag-over' : '',
          showingPreview ? 'preview-active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseEnter={disableHover ? undefined : onMouseEnter}
        onMouseMove={disableHover ? undefined : onMouseMove}
        onMouseLeave={disableHover ? undefined : onMouseLeave}
        onClick={onClick}
        onDragOver={(e) => {
          e.preventDefault();
          onDragOver(project.project_id);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          onDragOver(null);
        }}
        onDrop={(e) => {
          e.preventDefault();
          onDragOver(null);
          onDrop(e, project.project_id, project.project_coverImage);
        }}
        style={{
          opacity,
          animationDelay: `${idx * 0.1}s`,
          position: 'relative',
          ...(disableHover ? { transform: 'none' } : {}),
        }}
      >
        <div className="grid-image-wrapper" ref={wrapperRef}>
          {isUploadInProgress && (
            <div className="spinner-overlay">
              <div className="spinner" />
            </div>
          )}

          {!hasSrc && !isUploadInProgress && (
            <div className="grid-image-fallback" />
          )}

          {hasSrc && inView && (
            <img
              key={previewUrl || imageUrl}        // ← force React to remount on src change
              className="grid-image"
              src={previewUrl || imageUrl}
              alt={project.project_name}
              loading="lazy"
              onLoad={() => setLoaded(true)}
              onError={(e) => {
                // only hide broken images if they're not the freshly dropped preview
                if (!showingPreview) {
                  e.currentTarget.style.display = 'none';
                }
              }}
              style={{
                display: 'block',                  // override any stale display:none
                opacity: loaded ? 1 : 0,
                transition: disableHover
                  ? 'none'
                  : 'opacity 0.5s ease-in-out',
              }}
            />
          )}

          <div className="grid-title">
            <span className="grid-title-line">
              {project.project_name.toUpperCase()}
            </span>
            <br />
            <span className="grid-title-line">
              {project.project_director.toUpperCase()}
            </span>
            <br />
            <span className="grid-title-line">
              {project.project_company.toUpperCase()}
            </span>
          </div>
        </div>

        {showingPreview && !isUploadInProgress && (
          <div className="confirm-revert">
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                dropState.onConfirm();
              }}
            >
              Confirm
            </button>
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                dropState.onRevert();
              }}
            >
              Revert
            </button>
          </div>
        )}
      </div>
    );
  }
);

/* ---------- sortable wrapper (only used in admin) ---------- */
function SortableTile({
  project,
  idx,
  onClick,
  opacity,
  dropState,
  onDrop,
  onDragOver,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useSortable({
    id: project.project_id,
    animateLayoutChanges: () => false,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        zIndex: isDragging ? 999 : undefined,
      }}
      {...attributes}
      {...listeners}
    >
      <ImageTile
        project={project}
        idx={idx}
        onClick={onClick}
        opacity={opacity}
        disableHover
        dropState={dropState}
        onDrop={onDrop}
        onDragOver={onDragOver}
      />
    </div>
  );
}

/* ====================== ADMIN GRID proper ===================== */
const AdminGrid = forwardRef(({ allProjects, searchTerm }, ref) => {
  const [items, setItems] = useState([]);
  const [dropState, setDropState] = useState({
    projectId: null,
    dragOverId: null,
    previewUrl: null,
    loading: false,
    oldFilename: null,
    onConfirm: null,
    onRevert: null,
  });

  useEffect(() => {
    if (!allProjects) return;
    setItems(
      allProjects.filter((p) =>
        p.project_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [allProjects, searchTerm]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 50, tolerance: 5 },
    })
  );

  const apiBase =
    window.location.hostname === 'localhost'
      ? 'http://localhost:8000/gateway.php'
      : window.location.origin + '/backend/public/gateway.php';

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((p) => p.project_id === active.id);
    const newIdx = items.findIndex((p) => p.project_id === over.id);
    const reordered = arrayMove(items, oldIdx, newIdx);
    setItems(reordered);

    fetch(`${apiBase}?action=bulkUpdateOrder`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: reordered.map((p) => p.project_id) }),
    }).catch(console.error);
  };

  const popup = (id) =>
    window.open(
      `${window.location.origin}/content/?id=${id}`,
      '_blank',
      'width=675,height=600'
    );

  const handleDrop = async (e, projectId, oldFilename) => {
    const file = e.dataTransfer.files[0];
    if (!file) return;

    /* local preview */
    const img = document.createElement('img');
    const objectURL = URL.createObjectURL(file);
    img.src = objectURL;
    await img.decode();

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const scale = 600 / img.width;
    const h = img.height * scale;
    canvas.width = 600;
    canvas.height = 337;
    ctx.drawImage(img, 0, -(h - 337) / 2, 600, h);

    const previewDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setDropState({
      projectId,
      dragOverId: null,
      previewUrl: previewDataUrl,
      loading: true,
      oldFilename,
      onConfirm: null,
      onRevert: () =>
        setDropState({
          projectId: null,
          dragOverId: null,
          previewUrl: null,
          loading: false,
          oldFilename: null,
          onConfirm: null,
          onRevert: null,
        }),
    });

    canvas.toBlob(async (blob) => {
      const filename = `grid${Date.now()}.JPEG`;
      const form = new FormData();
      form.append('action', 'getUploadUrl');
      form.append('filename', filename);

      let uploadUrl, publicUrl;
      try {
        const res = await fetch(apiBase, {
          method: 'POST',
          credentials: 'include',
          body: form,
        });
        if (!res.ok) throw new Error(`Presign failed ${res.status}`);
        ({ url: uploadUrl, publicUrl } = await res.json());
      } catch (err) {
        alert('Error getting upload URL: ' + err.message);
        return setDropState((s) => ({ ...s, loading: false }));
      }

      try {
        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: blob,
        });
        if (!uploadRes.ok) throw new Error(`Upload failed ${uploadRes.status}`);
      } catch (err) {
        alert('Error uploading to S3: ' + err.message);
        return setDropState((s) => ({ ...s, loading: false }));
      }

      setDropState((s) => ({
        ...s,
        loading: false,
        onConfirm: () => confirmSwap(projectId, filename, oldFilename),
      }));
    }, 'image/jpeg', 0.9);
  };

  const handleDragOver = (projectId) => {
    setDropState((s) => ({ ...s, dragOverId: projectId }));
  };

  const confirmSwap = async (projectId, filename, oldFilename) => {
    if (oldFilename) {
      const s3Base =
        'https://arranigoe-website-grid-images.s3.us-east-2.amazonaws.com/';
      const oldUrl = s3Base + oldFilename.replace('gridTest/', '');
      try {
        const res = await fetch(oldUrl, { mode: 'cors' });
        if (res.ok) {
          const blob = await res.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = oldFilename.split('/').pop();
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(blobUrl);
        }
      } catch (e) {
        console.warn('[confirmSwap] Could not download old image:', e);
      }
    }

    const newKey = `gridTest/${filename}`;
    await fetch(`${apiBase}?action=updateProjectField`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: projectId,  
        field: 'project_coverImage',
        value: newKey,
      }),
    });

    setItems((list) =>
      list.map((p) =>
        p.project_id === projectId ? { ...p, project_coverImage: newKey } : p
      )
    );
    setDropState({
      projectId: null,
      dragOverId: null,
      previewUrl: null,
      loading: false,
      oldFilename: null,
      onConfirm: null,
      onRevert: null,
    });
  };

  useImperativeHandle(ref, () => ({}));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((p) => p.project_id)}
        strategy={rectSortingStrategy}
      >
        <div className="projects-grid">
          {items.map((p, idx) => (
            <SortableTile
              key={p.project_id}
              project={p}
              idx={idx}
              onClick={() => popup(p.project_id)}
              opacity={1}
              dropState={dropState}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
});

/* ------------------------------------------------------------------ */
/* ------------------------- PUBLIC GRID --------------------------- */
const PublicGrid = forwardRef(
  ({ allProjects, searchTerm, error, isTreatmentView, onTreatmentView }, ref) => {
    const [focused, setFocused] = useState(null);
    const [visible, setVisible] = useState([]);
    const [extraLinksVisible, setExtraLinksVisible] = useState(false);
    const hoverTimer = useRef(null);
    const initialPos = useRef({ x: 0, y: 0 });
    const progScroll = useRef(false);
    const refs = useRef({});

    useEffect(() => {
      if (!allProjects) return;
      setVisible(
        allProjects.filter((p) =>
          p.project_name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }, [allProjects, searchTerm]);

    useImperativeHandle(ref, () => ({
      scrollAndFocusProject(id) {
        setFocused(id);
        const container = document.querySelector(
          '.projects-scroll-container'
        );
        const el = refs.current[id];
        if (!container || !el) return;
        progScroll.current = true;
        const top =
          el.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop;
        container.scrollTo({ top, behavior: 'smooth' });
      },
    }));

    const startHover = (id, e) => {
      initialPos.current = { x: e.clientX, y: e.clientY };
      hoverTimer.current = setTimeout(() => {
        setFocused(id);
        hoverTimer.current = null;
      }, HOVER_DELAY);
    };
    const handleEnter = (e, id) => startHover(id, e);
    const handleMove = (e, id) => {
      const dx = Math.abs(e.clientX - initialPos.current.x),
        dy = Math.abs(e.clientY - initialPos.current.y);
      if (dx > MOVEMENT_THRESHOLD || dy > MOVEMENT_THRESHOLD) {
        clearTimeout(hoverTimer.current);
        startHover(id, e);
      }
    };
    const handleLeave = () => {
      clearTimeout(hoverTimer.current);
      setFocused(null);
      progScroll.current = false;
    };

    useEffect(() => {
      const c = document.querySelector('.projects-scroll-container');
      if (!c) return;
      const onScroll = () => {
        if (!progScroll.current) {
          clearTimeout(hoverTimer.current);
          setFocused(null);
        }
      };
      const onWheel = () => {
        setFocused(null);
        progScroll.current = false;
      };
      c.addEventListener('scroll', onScroll);
      c.addEventListener('wheel', onWheel);
      return () => {
        c.removeEventListener('scroll', onScroll);
        c.removeEventListener('wheel', onWheel);
      };
    }, []);

    const openPopup = (id) =>
      window.open(
        `${window.location.origin}/content/?id=${id}`,
        '_blank',
        'width=675,height=600'
      );

    return (
      <>
        {error && <p className="status-message error">{error}</p>}
        <div className="projects-grid">
          <div className="grid-item intro-item">
            <div className="intro-content">
              {isTreatmentView ? (
                <p className="intro-text" style={{ textAlign: 'left' }}>
                  I’ve been writing treatments for over 15 years, making me one of the most <span className="intro-white">experienced</span> and creative writers in the industry.<br/><br/>
                  No matter what genre, budget or timeline, I help <span className="intro-white">agencies</span>, <span className="intro-white">directors</span> and <span className="intro-white">production companies</span> maximise the impact of their pitches.<br/><br/>
                  Writing <span className="intro-white">over 500 treatments</span> so far, I’ve worked with some of the biggest <span className="intro-white">directors</span> in the business, supporting scores of award winning projects.<br/><br/>
                  I’ve written no less than three <span className="intro-white">Super Bowl</span> commercials, helped market two <span className="intro-white">Sundance</span> shortlisted features and even helped write for the <span className="intro-white">Oscars</span>.<br/><br/>
                  Please feel free to browse the work below, or click <span className="intro-white">
                    <a href="/aboutMe/index.html" style={{ textDecoration: 'none', color: 'inherit' }}>here</a>
                  </span> to find out more…
                </p>
              ) : (
                <>
                  <p className="intro-text">
                    <span className="intro-white">Creative Producer, Content Specialist</span>
                    <span className="intro-grey"> and </span>
                    <span
                      className="intro-white"
                      style={{ cursor: 'pointer', fontWeight: 'bold' }}
                      onClick={() => {
                        if (onTreatmentView) onTreatmentView(true);
                        setExtraLinksVisible(true);
                      }}
                    >
                      Treatment Writer
                    </span>
                    
                  </p>
                  <p></p>
                  <div className="intro-links">
                    {!extraLinksVisible ? (
                      <span
                        className="intro-link"
                        onClick={() => setExtraLinksVisible(true)}
                        style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 'bold' }}
                      >
                        [More...]
                      </span>
                    ) : (
                      <>
                      <span>+</span>
                        <span
                          className="intro-link"
                          onClick={() => {
                            if (onTreatmentView) onTreatmentView(true);
                            setExtraLinksVisible(true);
                          }}
                          style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 'bold' }}
                        >
                            
                          Treatment Writing
                        </span>
                        <span>+</span>
                        <a href="/otherWork/index.html" className="intro-link">Branded Content</a>
                        <span>+</span>
                        <a href="/aboutMe/index.html" className="intro-link">About Me</a>
                        <span>+</span>
                        <a href="mailto:arran.ig@gmail.com" className="intro-link">Contact</a>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {visible.map((p, idx) => (
            <ImageTile
              key={p.project_id}
              ref={(el) => (refs.current[p.project_id] = el)}
              project={p}
              idx={idx}
              onClick={() => openPopup(p.project_id)}
              onMouseEnter={(e) => handleEnter(e, p.project_id)}
              onMouseMove={(e) => handleMove(e, p.project_id)}
              onMouseLeave={handleLeave}
              opacity={focused && focused !== p.project_id ? 0.4 : 1}
            />
          ))}
        </div>
      </>
    );
  }
);

export default forwardRef((props, ref) => {
  const { isAdmin = false, ...rest } = props;
  return isAdmin ? (
    <AdminGrid {...rest} ref={ref} />
  ) : (
    <PublicGrid {...rest} ref={ref} />
  );
});